import { Text, Icon } from '@shopify/polaris';
import type { IconProps } from '@shopify/polaris';

type IconSource = IconProps['source'];

export interface HelpCategory {
  id: string;
  label: string;
  icon: IconSource;
  color: string;
}

interface HelpSidebarProps {
  categories: HelpCategory[];
  activeCategory: string;
  onSelect: (categoryId: string) => void;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export function HelpSidebar({
  categories,
  activeCategory,
  onSelect,
  isMobile = false,
  isOpen = true,
  onClose,
}: HelpSidebarProps) {
  const handleSelect = (categoryId: string) => {
    onSelect(categoryId);
    if (isMobile && onClose) {
      onClose();
    }
  };

  if (isMobile && !isOpen) {
    return null;
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
        />
      )}

      <nav
        style={{
          width: isMobile ? '280px' : '220px',
          position: isMobile ? 'fixed' : 'sticky',
          top: isMobile ? 0 : '80px',
          left: isMobile ? 0 : undefined,
          height: isMobile ? '100vh' : 'fit-content',
          maxHeight: isMobile ? undefined : 'calc(100vh - 100px)',
          backgroundColor: 'white',
          borderRadius: isMobile ? '0' : '12px',
          padding: '16px',
          border: isMobile ? 'none' : '1px solid #e1e3e5',
          boxShadow: isMobile ? '4px 0 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
          zIndex: isMobile ? 1000 : 1,
          flexShrink: 0,
          overflowY: 'auto',
        }}
      >
        {isMobile && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e1e3e5',
          }}>
            <Text as="span" variant="headingMd" fontWeight="bold">
              Help Center
            </Text>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                fontSize: '20px',
                color: '#637381',
              }}
              aria-label="Close menu"
            >
              &times;
            </button>
          </div>
        )}

        {!isMobile && (
          <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #e1e3e5' }}>
            <Text as="span" variant="headingSm" fontWeight="bold">
              Navigate
            </Text>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {categories.map((category) => {
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => handleSelect(category.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: isActive ? category.color : 'transparent',
                  color: isActive ? 'white' : '#202223',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 400,
                  transition: 'all 0.15s ease',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = '#f6f6f7';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isActive ? 'white' : category.color,
                  }}
                >
                  <Icon source={category.icon} />
                </div>
                <span>{category.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
